import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Egg, ArrowRight, ChatCircle } from '@phosphor-icons/react';
import AppNav from '../../components/AppNav';
import AppFooter from '../../components/AppFooter';
import PageMeta from '../../components/PageMeta';
import JsonLd, { softwareAppSchema } from '../../components/JsonLd';

function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

function formatDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatShortDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

export default function OvulationCalculator() {
  const today = new Date().toISOString().split('T')[0];
  const [lastPeriod, setLastPeriod] = useState('');
  const [cycleLength, setCycleLength] = useState(28);

  const results = useMemo(() => {
    if (!lastPeriod) return null;

    const startDate = new Date(lastPeriod + 'T00:00:00');
    if (isNaN(startDate.getTime())) return null;

    const cycles = [];
    for (let i = 0; i < 3; i++) {
      const cycleStart = addDays(startDate, cycleLength * i);
      const nextPeriod = addDays(cycleStart, cycleLength);
      const ovulationDay = addDays(nextPeriod, -14);
      const fertileStart = addDays(ovulationDay, -5);
      const fertileEnd = ovulationDay;
      const mostFertileStart = addDays(ovulationDay, -2);

      cycles.push({
        cycleNumber: i + 1,
        cycleStart,
        nextPeriod,
        ovulationDay,
        fertileStart,
        fertileEnd,
        mostFertileStart,
      });
    }

    return cycles;
  }, [lastPeriod, cycleLength]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white dark:from-gray-900 dark:to-gray-900">
      <PageMeta
        title="Ovulation Calculator — Find Your Fertile Window | Romy"
        description="Calculate your ovulation date and fertile window. Free ovulation predictor based on your cycle length."
        path="/tools/ovulation-calculator"
      />
      <JsonLd data={softwareAppSchema({
        name: 'Ovulation Calculator — Find Your Fertile Window | Romy',
        description: 'Calculate your ovulation date and fertile window. Free ovulation predictor based on your cycle length.',
        url: 'https://romyhealth.co/tools/ovulation-calculator',
      })} />
      <AppNav />

      <main className="pt-16 min-h-screen">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-orange-100 dark:bg-orange-900/40 mb-4">
              <Egg size={28} className="text-orange-500" weight="fill" />
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800 dark:text-gray-100 mb-2">
              Ovulation Calculator
            </h1>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 max-w-xl mx-auto">
              Estimate your ovulation date and fertile window to understand your cycle and plan accordingly.
            </p>
          </div>

          {/* Calculator Card */}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-pink-100 dark:border-pink-800 p-6 sm:p-8 mb-8">
            <div className="space-y-6">
              {/* Last Period Date */}
              <div>
                <label htmlFor="lastPeriod" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  First day of your last period
                </label>
                <input
                  type="date"
                  id="lastPeriod"
                  value={lastPeriod}
                  max={today}
                  onChange={(e) => setLastPeriod(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-400 transition-colors"
                />
              </div>

              {/* Cycle Length */}
              <div>
                <label htmlFor="cycleLength" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Average cycle length (days)
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    id="cycleLengthRange"
                    min={21}
                    max={40}
                    value={cycleLength}
                    onChange={(e) => setCycleLength(Number(e.target.value))}
                    className="flex-1 accent-pink-500"
                  />
                  <input
                    type="number"
                    id="cycleLength"
                    min={21}
                    max={45}
                    value={cycleLength}
                    onChange={(e) => setCycleLength(Math.max(21, Math.min(45, Number(e.target.value))))}
                    className="w-20 px-3 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-100 text-center focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-pink-400 transition-colors"
                  />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Most cycles are 21 to 35 days. The average is 28 days.
                </p>
              </div>
            </div>
          </div>

          {/* Results */}
          {results && (
            <div className="space-y-4 mb-8">
              <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
                Your Ovulation & Fertile Windows
              </h2>

              {results.map((cycle) => (
                <div
                  key={cycle.cycleNumber}
                  className="bg-gradient-to-r from-orange-50 to-pink-50 dark:from-orange-950/40 dark:to-pink-950/40 rounded-2xl border border-orange-100 dark:border-orange-800 p-5 sm:p-6"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-orange-500 text-white text-xs font-bold">
                      {cycle.cycleNumber}
                    </span>
                    <h3 className="font-semibold text-gray-800 dark:text-gray-100">
                      Cycle {cycle.cycleNumber}
                    </h3>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs font-medium text-orange-600 dark:text-orange-400 uppercase tracking-wide mb-1">
                        Estimated Ovulation
                      </p>
                      <p className="text-sm text-gray-800 dark:text-gray-100 font-medium">
                        {formatDate(cycle.ovulationDay)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-pink-600 dark:text-pink-400 uppercase tracking-wide mb-1">
                        Fertile Window
                      </p>
                      <p className="text-sm text-gray-800 dark:text-gray-100 font-medium">
                        {formatShortDate(cycle.fertileStart)} &ndash; {formatShortDate(cycle.fertileEnd)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-rose-600 dark:text-rose-400 uppercase tracking-wide mb-1">
                        Peak Fertility
                      </p>
                      <p className="text-sm text-gray-800 dark:text-gray-100 font-medium">
                        {formatShortDate(cycle.mostFertileStart)} &ndash; {formatShortDate(cycle.ovulationDay)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}

              {/* Key */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-5 sm:p-6">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100 mb-3">
                  Understanding your fertile window
                </h3>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  <li className="flex items-start gap-2">
                    <span className="inline-block w-3 h-3 rounded-full bg-orange-400 mt-1 flex-shrink-0" />
                    <span><strong>Ovulation</strong> typically occurs about 14 days before your next period starts. This is when an egg is released.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="inline-block w-3 h-3 rounded-full bg-pink-400 mt-1 flex-shrink-0" />
                    <span><strong>Fertile window</strong> is the 6-day span ending on ovulation day. Sperm can survive up to 5 days, so conception is possible throughout this window.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="inline-block w-3 h-3 rounded-full bg-rose-400 mt-1 flex-shrink-0" />
                    <span><strong>Peak fertility</strong> is the 2-3 days just before and including ovulation, when conception is most likely.</span>
                  </li>
                </ul>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-4 leading-relaxed">
                  These are estimates based on average cycle calculations. Factors like stress, illness, and hormonal changes can shift your actual ovulation date. For more accurate tracking, consider monitoring basal body temperature or using ovulation predictor kits.
                </p>
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="bg-gradient-to-r from-orange-500 to-pink-500 rounded-2xl p-6 sm:p-8 text-center text-white">
            <ChatCircle size={32} weight="fill" className="mx-auto mb-3" />
            <h3 className="text-lg sm:text-xl font-bold mb-2">
              Track this automatically with Romy
            </h3>
            <p className="text-white/90 text-sm mb-5 max-w-md mx-auto">
              Tell Romy about your cycle and she will estimate your fertile windows, track patterns, and remind you of key dates.
            </p>
            <Link
              to="/chat"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-pink-600 font-semibold rounded-lg hover:bg-pink-50 transition-colors shadow-md"
            >
              Chat with Romy
              <ArrowRight size={18} weight="bold" />
            </Link>
          </div>
        </div>
      </main>

      <AppFooter />
    </div>
  );
}
